import { AiOutlineCodepen, AiOutlineFileText } from 'react-icons/ai';
import { FaSnowflake } from 'react-icons/fa';
import { FiCodesandbox } from 'react-icons/fi';
import { RiBracesLine } from 'react-icons/ri';
import { match } from 'ts-pattern';

import { Icon } from '@/components/icon/icon';
import {
  Source,
  SourceAgentLogs,
  SourceCompletionLogs,
  SourceJson,
  SourceModelInvocationLogs,
  SourceSnowflake
} from '@/types/evals';

import { ConfigBlockShell as BlockShell, emptyVal, Field } from '../../_components/ConfigBlockShell';

export function JsonConfigBlock( { source }: { source: SourceJson } ) {
  const entries = Object.entries( source.config );
  if ( entries.length === 0 ) return null;
  return (
    <BlockShell title="JSON Details" icon={ <Icon icon={ RiBracesLine } /> }>
      { entries.map( ( [ key, value ] ) => (
        <Field key={ key } label={ key } value={ emptyVal( value ) } />
      ) ) }
    </BlockShell>
  );
}

export function SnowflakeConfigBlock( { source }: { source: SourceSnowflake } ) {
  const { config } = source;
  return (
    <BlockShell title="Snowflake Details" icon={ <Icon icon={ FaSnowflake } /> }>
      <Field label="Database Name" value={ emptyVal( config.database_name ) } />
      <Field label="Schema Name" value={ emptyVal( config.schema_name ) } />
      <Field label="Credential Reference" value={ emptyVal( config.credential_ref ) } />
      <Field label="Query" value={ emptyVal( config.query ) } full />
    </BlockShell>
  );
}

export function AgentActionConfigBlock( { source }: { source: SourceAgentLogs } ) {
  const { filters, row_limit } = source.config;
  return (
    <BlockShell title="Agent Action Log Details" icon={ <Icon icon={ FiCodesandbox } /> }>
      <Field label="Constellation Name" value={ emptyVal( filters.constellation_name ) } />
      <Field label="Agent Name" value={ emptyVal( filters.agent_name ) } />
      <Field label="Agent ID" value={ emptyVal( filters.agent_id ) } />
      <Field label="Component Name" value={ emptyVal( filters.component_name ) } />
      <Field label="Constellation Version" value={ emptyVal( filters.constellation_version ) } />
      <Field label="Client ID" value={ emptyVal( filters.client_id ) } />
      <Field label="Correlation ID" value={ emptyVal( filters.correlation_id ) } />
      <Field label="Request ID" value={ emptyVal( filters.request_id ) } />
      <Field label="From Start Time" value={ emptyVal( filters.from_start_time ) } />
      <Field label="To End Time" value={ emptyVal( filters.to_end_time ) } />
      <Field label="Row Limit" value={ emptyVal( row_limit ) } />
    </BlockShell>
  );
}

export function ModelInvocationConfigBlock( { source }: { source: SourceModelInvocationLogs } ) {
  const { filters, row_limit, context_window } = source.config;
  return (
    <BlockShell title="Model Invocation Log Details" icon={ <Icon icon={ AiOutlineCodepen } /> }>
      <Field label="Constellation Name" value={ emptyVal( filters.constellation_name ) } />
      <Field label="Agent Name" value={ emptyVal( filters.agent_name ) } />
      <Field label="Agent ID" value={ emptyVal( filters.agent_id ) } />
      <Field label="Constellation Version" value={ emptyVal( filters.constellation_version ) } />
      <Field label="Client ID" value={ emptyVal( filters.client_id ) } />
      <Field label="Correlation ID" value={ emptyVal( filters.correlation_id ) } />
      <Field label="Request ID" value={ emptyVal( filters.request_id ) } />
      <Field label="From Start Time" value={ emptyVal( filters.from_start_time ) } />
      <Field label="To End Time" value={ emptyVal( filters.to_end_time ) } />
      <Field label="Row Limit" value={ emptyVal( row_limit ) } />
      <Field label="Context Window" value={ emptyVal( context_window ) } />
    </BlockShell>
  );
}

export function RequestResponseConfigBlock( { source }: { source: SourceCompletionLogs } ) {
  const { filters, row_limit } = source.config;
  return (
    <BlockShell title="Request/Response Log Details" icon={ <Icon icon={ AiOutlineFileText } /> }>
      <Field label="Constellation Name" value={ emptyVal( filters.constellation_name ) } />
      <Field label="Agent Name" value={ emptyVal( filters.agent_name ) } />
      <Field label="Agent ID" value={ emptyVal( filters.agent_id ) } />
      <Field label="Constellation Version" value={ emptyVal( filters.constellation_version ) } />
      <Field label="Client ID" value={ emptyVal( filters.client_id ) } />
      <Field label="Request ID" value={ emptyVal( filters.request_id ) } />
      <Field label="From Start Time" value={ emptyVal( filters.from_start_time ) } />
      <Field label="To End Time" value={ emptyVal( filters.to_end_time ) } />
      <Field label="Row Limit" value={ emptyVal( row_limit ) } />
    </BlockShell>
  );
}

export function ConfigBlock( { source }: { source: Source } ) {
  return match( source )
    .with( { source_type: 'json' }, ( s ) => <JsonConfigBlock source={ s } /> )
    .with( { source_type: 'snowflake' }, ( s ) => <SnowflakeConfigBlock source={ s } /> )
    .with( { source_type: 'mas_agent_action_log' }, ( s ) => <AgentActionConfigBlock source={ s } /> )
    .with( { source_type: 'mas_model_invocation_log' }, ( s ) => <ModelInvocationConfigBlock source={ s } /> )
    .with( { source_type: 'mas_completion_log' }, ( s ) => <RequestResponseConfigBlock source={ s } /> )
    .exhaustive( () => (
      <BlockShell title="Unsupported Source Type" icon={ <Icon icon={ FiCodesandbox } /> }>
        <Field label="Source Type" value={ emptyVal( source.source_type ) } full />
      </BlockShell>
    ) );
}
